import { fetchWrapper } from "@/helpers/fetch-wrapper";

// Czas życia cache listy lokalizacji (ms). Lokalizacje zmieniają się rzadko, a lista jest pobierana
// przez wiele widoków (filtry, minimapa w szczegółach pojazdu).
const LOCATIONS_CACHE_TTL = 60000;

/**
 * Serwis obsługujący pobieranie danych do filtrów (np. lista lokalizacji, użytkowników)
 */
class FiltersService {
  
  /**
   * Pobiera listę lokalizacji z API.
   * - Wysyła GET na endpoint `/locations`.
   * - Odwraca kolejność lokalizacji (`reverse()`), najnowsze będą pierwsze.
   * - Wynik jest krótko cache'owany, a równoległe wywołania współdzielą jedno żądanie. Lista lokalizacji
   *   zmienia się rzadko, a była pobierana ponownie przy każdym wejściu w widok korzystający z filtrów.
   * @returns {Promise<Array>} Tablica lokalizacji
   */
  async getLocations() {
    const now = Date.now();

    // Świeży wynik z cache — bez odpytywania API.
    if (this._locationsCache && now - this._locationsCacheAt < LOCATIONS_CACHE_TTL) {
      return this._locationsCache;
    }

    // Żądanie już leci — dołączamy się do niego zamiast wysyłać kolejne.
    if (this._locationsPromise) {
      return this._locationsPromise;
    }

    this._locationsPromise = fetchWrapper
      .get(`${process.env.VUE_APP_API_URL}/locations`)
      .then((locations) => {
        const result = Array.isArray(locations) ? locations.reverse() : locations;
        this._locationsCache = result;
        this._locationsCacheAt = Date.now();
        return result;
      })
      .finally(() => {
        this._locationsPromise = null;
      });

    return this._locationsPromise;
  }

  /**
   * Czyści cache listy lokalizacji. Wywoływane po zmianach w konfiguracji lokalizacji,
   * żeby zmiany były widoczne od razu.
   */
  invalidateLocations() {
    this._locationsCache = null;
    this._locationsCacheAt = 0;
    this._locationsPromise = null;
  }

  /**
   * Pobiera listę użytkowników z API.
   * - Wysyła GET na endpoint `/users` z limitem 1000 użytkowników.
   * @returns {Promise<Array>} Tablica użytkowników
   */
  getUsers() {
    return fetchWrapper.get(`${process.env.VUE_APP_API_URL}/users?page=1&limit=1000`);
  }

  /**
   * Pobiera dane filtrów dla historii aktywności użytkowników.
   * Zawiera listę użytkowników oraz dostępne typy zdarzeń.
   * @returns {Promise<{users:Array,eventTypes:Object}>}
   */
  getUserActivityFilterValues() {
    return fetchWrapper.get(`${process.env.VUE_APP_API_URL}/login-to-locations/filter-values`);
  }
}

// Eksportujemy pojedynczy (singleton) obiekt serwisu
export default new FiltersService();
